import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import { useSession } from '@/stores/session';
import * as api from './api';
import type { PersonPresence, PresenceDto, PresenceMember, PresenceSettings } from './types';

export const presenceKeys = {
  all: ['presence'] as const,
  org: () => [...presenceKeys.all, 'org'] as const,
  settings: () => [...presenceKeys.all, 'settings'] as const,
};

/** Staff tabs re-read presence this often; live join/leave fills the gaps. */
const PRESENCE_REFRESH_MS = 30_000;

/** Only staff see presence; guests are counted as online but never see it
 *  (backend ADR-107 — the endpoint answers them 403). */
export function useCanSeePresence(): boolean {
  return useSession((s) => s.isAuthenticated && s.role !== null && s.role !== 'guest');
}

function presenceQuery(enabled: boolean) {
  return {
    queryKey: presenceKeys.org(),
    queryFn: ({ signal }: { signal: AbortSignal }) => api.getPresence(signal),
    enabled,
    staleTime: PRESENCE_REFRESH_MS,
    retry: false,
  } as const;
}

/** The org's presence, re-read every 30 s — mounted once, by `PresenceSync`. */
export function useOrgPresence() {
  const enabled = useCanSeePresence();
  return useQuery({
    ...presenceQuery(enabled),
    refetchInterval: enabled ? PRESENCE_REFRESH_MS : false,
  });
}

/**
 * One person's status for an avatar, by member id or user id. Null when there
 * is nothing honest to show: the viewer is a guest, presence hasn't loaded,
 * realtime is down (nobody can be shown online), or they aren't in this org.
 */
// Boards put a badge on every avatar: index each list once, not per badge.
const indexes = new WeakMap<PresenceDto, { byMember: Map<number, PresenceMember>; byUser: Map<number, PresenceMember> }>();

function indexOf(dto: PresenceDto) {
  let index = indexes.get(dto);
  if (!index) {
    index = {
      byMember: new Map(dto.members.map((m) => [m.member, m])),
      byUser: new Map(dto.members.map((m) => [m.user, m])),
    };
    indexes.set(dto, index);
  }
  return index;
}

export function usePresence({ member, user }: { member?: number | null; user?: number | null }) {
  const enabled = useCanSeePresence() && (member != null || user != null);
  const { data } = useQuery({
    ...presenceQuery(enabled),
    select: (dto: PresenceDto): PersonPresence | null => {
      if (!dto.live) return null;
      const index = indexOf(dto);
      const row = member != null ? index.byMember.get(member) : index.byUser.get(user!);
      return row ? { online: row.online, lastSeenAt: row.last_seen_at } : null;
    },
  });
  return data ?? null;
}

export function usePresenceSettings() {
  const enabled = useSession((s) => s.isAuthenticated && s.activeOrgSlug !== null);
  return useQuery({
    queryKey: presenceKeys.settings(),
    queryFn: api.getPresenceSettings,
    enabled,
    staleTime: Infinity,
  });
}

/** Saving reconnects the caller's tabs, which rejoin presence with the new
 *  choice (backend ADR-107) — nothing else to do here. */
export function useUpdatePresenceSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PresenceSettings) => api.updatePresenceSettings(input),
    onSuccess: (settings) => {
      qc.setQueryData(presenceKeys.settings(), settings);
      void qc.invalidateQueries({ queryKey: presenceKeys.org() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
